<section class="offer-setup" aria-labelledby="offer-setup-heading">
  <header class="offer-setup-heading">
    <div>
      <h2 id="offer-setup-heading">Offer Setup</h2>
      <p>Enable Custom Payments for the desired select custom offers. All data left unedited will preserve default payments.</p>
    </div>

    <span class="readiness-badge" [class.ready]="isReady()">
      @if (isReady()) {
        Ready to stage
      } @else {
        Not ready
      }
    </span>
  </header>

  <p class="screen-reader-only" aria-live="polite" aria-atomic="true">
    {{ readinessMessage }}
  </p>

  @if (initializationMessage !== '') {
    <div class="initialization-state" role="status">
      <strong>Offer Setup unavailable</strong>
      <span>{{ initializationMessage }}</span>
    </div>
  }

  <form [formGroup]="offerSetupForm" (ngSubmit)="onStage()" novalidate>
    <div class="offer-sections">
      <fieldset class="offer-section"
                formGroupName="lease"
                [class.custom-section]="offerSetupForm.controls.lease.controls.isCustom.value"
                [class.default-section]="!offerSetupForm.controls.lease.controls.isCustom.value">
        <legend>{{ sectionLabels.Lease }}</legend>

        <div class="toggle-row">
          <div>
            <label for="lease-custom-toggle">Customize {{ sectionLabels.Lease }} offer</label>
            <span id="lease-custom-state" class="section-state">
              Source/default values are preserved for all offer sets unless manually selected and adjusted
            </span>
          </div>
          <input id="lease-custom-toggle"
                 type="checkbox"
                 role="switch"
                 [checked]="offerSetupForm.controls.lease.controls.isCustom.value"
                 [disabled]="!isInitialized()"
                 aria-describedby="lease-custom-state"
                 (change)="onLeaseCustomToggle()">
        </div>

        <div class="field-grid">
          <div class="form-field">
            <label for="lease-payment-amount">Payment amount</label>
            <input id="lease-payment-amount"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="paymentAmount"
                   aria-describedby="lease-payment-amount-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.lease.controls.paymentAmount)">
            @if (shouldShowError(offerSetupForm.controls.lease.controls.paymentAmount)) {
              <span id="lease-payment-amount-error" class="field-error">
                Enter a non-negative decimal; zero and values such as .5 are allowed.
              </span>
            }
          </div>

          <fieldset class="money-down-mode">
            <legend>Money-down mode</legend>
            <label>
              <input type="radio"
                     name="lease-money-down-mode"
                     [checked]="leaseMoneyDownMode === moneyDownModes.Dollar"
                     [disabled]="!offerSetupForm.controls.lease.controls.isCustom.value"
                     (change)="onLeaseDollarMode(leaseMoneyDownInput)">
              Dollar
            </label>
            <label>
              <input type="radio"
                     name="lease-money-down-mode"
                     [checked]="leaseMoneyDownMode === moneyDownModes.Percentage"
                     [disabled]="!offerSetupForm.controls.lease.controls.isCustom.value"
                     (change)="onLeasePercentageMode(leasePercentageDownInput)">
              Percentage
            </label>
          </fieldset>

          <div class="form-field">
            <label for="lease-money-down">Money down</label>
            <input #leaseMoneyDownInput
                   id="lease-money-down"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="moneyDown"
                   aria-describedby="lease-money-down-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.lease.controls.moneyDown)">
            @if (shouldShowError(offerSetupForm.controls.lease.controls.moneyDown)) {
              <span id="lease-money-down-error" class="field-error">Enter a non-negative decimal.</span>
            }
          </div>

          <div class="form-field">
            <label for="lease-percentage-down">Percentage down</label>
            <input #leasePercentageDownInput
                   id="lease-percentage-down"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="percentageDown"
                   aria-describedby="lease-percentage-down-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.lease.controls.percentageDown)">
            @if (shouldShowError(offerSetupForm.controls.lease.controls.percentageDown)) {
              <span id="lease-percentage-down-error" class="field-error">Enter a non-negative decimal.</span>
            }
          </div>

          <div class="form-field">
            <label for="lease-term">Term</label>
            <input id="lease-term"
                   pInputText
                   type="text"
                   inputmode="numeric"
                   formControlName="term"
                   aria-describedby="lease-term-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.lease.controls.term)">
            @if (shouldShowError(offerSetupForm.controls.lease.controls.term)) {
              <span id="lease-term-error" class="field-error">Enter a safe whole number greater than zero.</span>
            }
          </div>

          <div class="form-field">
            <label for="lease-annual-mileage">Annual mileage</label>
            <input id="lease-annual-mileage"
                   pInputText
                   type="text"
                   inputmode="numeric"
                   formControlName="annualMileage"
                   aria-describedby="lease-annual-mileage-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.lease.controls.annualMileage)">
            @if (shouldShowError(offerSetupForm.controls.lease.controls.annualMileage)) {
              <span id="lease-annual-mileage-error" class="field-error">Enter a non-negative safe whole number.</span>
            }
          </div>

          <div class="form-field full-width">
            <label for="lease-additional-text">Additional text</label>
            <textarea id="lease-additional-text"
                      pTextarea
                      rows="3"
                      formControlName="additionalText"></textarea>
          </div>
        </div>
      </fieldset>

      <fieldset class="offer-section"
                formGroupName="finance"
                [class.custom-section]="offerSetupForm.controls.finance.controls.isCustom.value"
                [class.default-section]="!offerSetupForm.controls.finance.controls.isCustom.value">
        <legend>{{ sectionLabels.Finance }}</legend>

        <div class="toggle-row">
          <div>
            <label for="finance-custom-toggle">Customize {{ sectionLabels.Finance }} offer</label>
            <span id="finance-custom-state" class="section-state">
              Source/default values are preserved for all offer sets unless manually selected and adjusted
            </span>
          </div>
          <input id="finance-custom-toggle"
                 type="checkbox"
                 role="switch"
                 [checked]="offerSetupForm.controls.finance.controls.isCustom.value"
                 [disabled]="!isInitialized()"
                 aria-describedby="finance-custom-state"
                 (change)="onFinanceCustomToggle()">
        </div>

        <div class="field-grid">
          <div class="form-field">
            <label for="finance-payment-amount">Payment amount</label>
            <input id="finance-payment-amount"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="paymentAmount"
                   aria-describedby="finance-payment-amount-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.finance.controls.paymentAmount)">
            @if (shouldShowError(offerSetupForm.controls.finance.controls.paymentAmount)) {
              <span id="finance-payment-amount-error" class="field-error">
                Enter a non-negative decimal; zero and values such as .5 are allowed.
              </span>
            }
          </div>

          <fieldset class="money-down-mode">
            <legend>Money-down mode</legend>
            <label>
              <input type="radio"
                     name="finance-money-down-mode"
                     [checked]="financeMoneyDownMode === moneyDownModes.Dollar"
                     [disabled]="!offerSetupForm.controls.finance.controls.isCustom.value"
                     (change)="onFinanceDollarMode(financeMoneyDownInput)">
              Dollar
            </label>
            <label>
              <input type="radio"
                     name="finance-money-down-mode"
                     [checked]="financeMoneyDownMode === moneyDownModes.Percentage"
                     [disabled]="!offerSetupForm.controls.finance.controls.isCustom.value"
                     (change)="onFinancePercentageMode(financePercentageDownInput)">
              Percentage
            </label>
          </fieldset>

          <div class="form-field">
            <label for="finance-money-down">Money down</label>
            <input #financeMoneyDownInput
                   id="finance-money-down"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="moneyDown"
                   aria-describedby="finance-money-down-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.finance.controls.moneyDown)">
            @if (shouldShowError(offerSetupForm.controls.finance.controls.moneyDown)) {
              <span id="finance-money-down-error" class="field-error">Enter a non-negative decimal.</span>
            }
          </div>

          <div class="form-field">
            <label for="finance-percentage-down">Percentage down</label>
            <input #financePercentageDownInput
                   id="finance-percentage-down"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="percentageDown"
                   aria-describedby="finance-percentage-down-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.finance.controls.percentageDown)">
            @if (shouldShowError(offerSetupForm.controls.finance.controls.percentageDown)) {
              <span id="finance-percentage-down-error" class="field-error">Enter a non-negative decimal.</span>
            }
          </div>

          <div class="form-field">
            <label for="finance-apr">APR</label>
            <input id="finance-apr"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="apr"
                   aria-describedby="finance-apr-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.finance.controls.apr)">
            @if (shouldShowError(offerSetupForm.controls.finance.controls.apr)) {
              <span id="finance-apr-error" class="field-error">Enter a non-negative decimal.</span>
            }
          </div>

          <div class="form-field">
            <label for="finance-term">Term</label>
            <input id="finance-term"
                   pInputText
                   type="text"
                   inputmode="numeric"
                   formControlName="term"
                   aria-describedby="finance-term-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.finance.controls.term)">
            @if (shouldShowError(offerSetupForm.controls.finance.controls.term)) {
              <span id="finance-term-error" class="field-error">Enter a safe whole number greater than zero.</span>
            }
          </div>

          <div class="form-field full-width">
            <label for="finance-additional-text">Additional text</label>
            <textarea id="finance-additional-text"
                      pTextarea
                      rows="3"
                      formControlName="additionalText"></textarea>
          </div>
        </div>
      </fieldset>

      <fieldset class="offer-section"
                formGroupName="alternateLease"
                [class.custom-section]="offerSetupForm.controls.alternateLease.controls.isCustom.value"
                [class.default-section]="!offerSetupForm.controls.alternateLease.controls.isCustom.value">
        <legend>{{ sectionLabels.AlternateLease }}</legend>

        <div class="toggle-row">
          <div>
            <label for="alternate-lease-custom-toggle">Customize {{ sectionLabels.AlternateLease }} offer</label>
            <span id="alternate-lease-custom-state" class="section-state">
              Source/default values are preserved for all offer sets unless manually selected and adjusted
            </span>
          </div>
          <input id="alternate-lease-custom-toggle"
                 type="checkbox"
                 role="switch"
                 [checked]="offerSetupForm.controls.alternateLease.controls.isCustom.value"
                 [disabled]="!isInitialized()"
                 aria-describedby="alternate-lease-custom-state"
                 (change)="onAlternateLeaseCustomToggle()">
        </div>

        <div class="field-grid">
          <div class="form-field">
            <label for="alternate-lease-payment-amount">Payment amount</label>
            <input id="alternate-lease-payment-amount"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="paymentAmount"
                   aria-describedby="alternate-lease-payment-amount-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.alternateLease.controls.paymentAmount)">
            @if (shouldShowError(offerSetupForm.controls.alternateLease.controls.paymentAmount)) {
              <span id="alternate-lease-payment-amount-error" class="field-error">
                Enter a non-negative decimal; zero and values such as .5 are allowed.
              </span>
            }
          </div>

          <fieldset class="money-down-mode">
            <legend>Money-down mode</legend>
            <label>
              <input type="radio"
                     name="alternate-lease-money-down-mode"
                     [checked]="alternateLeaseMoneyDownMode === moneyDownModes.Dollar"
                     [disabled]="!offerSetupForm.controls.alternateLease.controls.isCustom.value"
                     (change)="onAlternateLeaseDollarMode(alternateLeaseMoneyDownInput)">
              Dollar
            </label>
            <label>
              <input type="radio"
                     name="alternate-lease-money-down-mode"
                     [checked]="alternateLeaseMoneyDownMode === moneyDownModes.Percentage"
                     [disabled]="!offerSetupForm.controls.alternateLease.controls.isCustom.value"
                     (change)="onAlternateLeasePercentageMode(alternateLeasePercentageDownInput)">
              Percentage
            </label>
          </fieldset>

          <div class="form-field">
            <label for="alternate-lease-money-down">Money down</label>
            <input #alternateLeaseMoneyDownInput
                   id="alternate-lease-money-down"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="moneyDown"
                   aria-describedby="alternate-lease-money-down-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.alternateLease.controls.moneyDown)">
            @if (shouldShowError(offerSetupForm.controls.alternateLease.controls.moneyDown)) {
              <span id="alternate-lease-money-down-error" class="field-error">Enter a non-negative decimal.</span>
            }
          </div>

          <div class="form-field">
            <label for="alternate-lease-percentage-down">Percentage down</label>
            <input #alternateLeasePercentageDownInput
                   id="alternate-lease-percentage-down"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="percentageDown"
                   aria-describedby="alternate-lease-percentage-down-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.alternateLease.controls.percentageDown)">
            @if (shouldShowError(offerSetupForm.controls.alternateLease.controls.percentageDown)) {
              <span id="alternate-lease-percentage-down-error" class="field-error">Enter a non-negative decimal.</span>
            }
          </div>

          <div class="form-field">
            <label for="alternate-lease-term">Term</label>
            <input id="alternate-lease-term"
                   pInputText
                   type="text"
                   inputmode="numeric"
                   formControlName="term"
                   aria-describedby="alternate-lease-term-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.alternateLease.controls.term)">
            @if (shouldShowError(offerSetupForm.controls.alternateLease.controls.term)) {
              <span id="alternate-lease-term-error" class="field-error">Enter a safe whole number greater than zero.</span>
            }
          </div>

          <div class="form-field">
            <label for="alternate-lease-annual-mileage">Annual mileage</label>
            <input id="alternate-lease-annual-mileage"
                   pInputText
                   type="text"
                   inputmode="numeric"
                   formControlName="annualMileage"
                   aria-describedby="alternate-lease-annual-mileage-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.alternateLease.controls.annualMileage)">
            @if (shouldShowError(offerSetupForm.controls.alternateLease.controls.annualMileage)) {
              <span id="alternate-lease-annual-mileage-error" class="field-error">Enter a non-negative safe whole number.</span>
            }
          </div>

          <div class="form-field full-width">
            <label for="alternate-lease-additional-text">Additional text</label>
            <textarea id="alternate-lease-additional-text"
                      pTextarea
                      rows="3"
                      formControlName="additionalText"></textarea>
          </div>
        </div>
      </fieldset>

      <fieldset class="offer-section"
                formGroupName="promotionalApr"
                [class.custom-section]="!offerSetupForm.controls.promotionalApr.controls.useDefaultSettings.value"
                [class.default-section]="offerSetupForm.controls.promotionalApr.controls.useDefaultSettings.value">
        <legend>{{ sectionLabels.PromotionalApr }}</legend>

        <div class="toggle-row">
          <div>
            <label for="promotional-apr-custom-toggle">Customize {{ sectionLabels.PromotionalApr }} offer</label>
            <span id="promotional-apr-custom-state" class="section-state">
              Source/default values are preserved for all offer sets unless manually selected and adjusted
            </span>
          </div>
          <input id="promotional-apr-custom-toggle"
                 type="checkbox"
                 role="switch"
                 [checked]="!offerSetupForm.controls.promotionalApr.controls.useDefaultSettings.value"
                 [disabled]="!isInitialized()"
                 aria-describedby="promotional-apr-custom-state"
                 (change)="onPromotionalAprCustomToggle()">
        </div>

        <div class="field-grid">
          <div class="form-field">
            <label for="promotional-apr-apr">APR</label>
            <input id="promotional-apr-apr"
                   pInputText
                   type="text"
                   inputmode="decimal"
                   formControlName="apr"
                   aria-describedby="promotional-apr-apr-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.promotionalApr.controls.apr)">
            @if (shouldShowError(offerSetupForm.controls.promotionalApr.controls.apr)) {
              <span id="promotional-apr-apr-error" class="field-error">
                @if (offerSetupForm.controls.promotionalApr.controls.apr.hasError(validationKeys.Required)) {
                  APR is required while custom.
                } @else {
                  Enter a non-negative decimal; zero is allowed.
                }
              </span>
            }
          </div>

          <div class="form-field">
            <label for="promotional-apr-term">Term</label>
            <input id="promotional-apr-term"
                   pInputText
                   type="text"
                   inputmode="numeric"
                   formControlName="term"
                   aria-describedby="promotional-apr-term-error"
                   [attr.aria-invalid]="shouldShowError(offerSetupForm.controls.promotionalApr.controls.term)">
            @if (shouldShowError(offerSetupForm.controls.promotionalApr.controls.term)) {
              <span id="promotional-apr-term-error" class="field-error">
                @if (offerSetupForm.controls.promotionalApr.controls.term.hasError(validationKeys.Required)) {
                  Term is required while custom.
                } @else {
                  Enter a safe whole number greater than zero; zero is not valid.
                }
              </span>
            }
          </div>

          <div class="form-field full-width">
            <label for="promotional-apr-additional-text">Additional text</label>
            <textarea id="promotional-apr-additional-text"
                      pTextarea
                      rows="3"
                      formControlName="additionalText"></textarea>
          </div>
        </div>
      </fieldset>

      <fieldset class="offer-section">
        <legend>Multi-VIN Visibility</legend>
        <div class="toggle-row">
          <label>
            <input type="radio"
                   formControlName="multiVinVisibility"
                   [value]="visibilityScopes.FullVisibility">
            Full Visibility
          </label>
          <label>
            <input type="radio"
                   formControlName="multiVinVisibility"
                   [value]="visibilityScopes.InventoryOnly">
            Inventory Only
          </label>
        </div>
      </fieldset>
    </div>

    @if (stageErrorMessage !== '') {
      <div class="stage-error" role="alert">
        {{ stageErrorMessage }}
      </div>
    }

    @if (isInitialized() && !hasCustomOfferConfigured()) {
      <p class="stage-error" role="status">Please configure at least one custom offer to continue.</p>
    }

    <footer class="form-actions">
      <button type="button" class="secondary-action" (click)="onBack()">Back</button>
      <button type="submit" class="primary-action" [disabled]="!isReady()">Stage</button>
    </footer>
  </form>
</section>
